import type { CompanyJobSummary } from './job';
import type { CVConfigData, CvSectionId } from './cv';
import type { PublicProfileEffectivePrivacy } from './profile-privacy';
import type {
  CareerHighlightDraft,
  SkillGroupDraft,
  SmartCVPublicCertification,
  SmartCVPublicEducation,
  SmartCVPublicExperience,
  SmartCVPublicProject,
  SmartCVPublicSocials,
} from './identity';

export type PublicProfileOwnerKind = 'user' | 'company';
export type PublicProfileMode = 'individual' | 'company';

export interface PublicUserProfileUrls {
  hub: string;
  cv: string;
  resumeJson: string;
  displayHandle: string;
}

export interface PublicCompanyProfileUrls {
  hub: string;
  jobs: string;
  displayHandle: string;
  /** App handoff for the talent-network join intent (connect-style dialog). */
  appJoin?: string;
}

export type PublicProfileUrls = PublicUserProfileUrls | PublicCompanyProfileUrls;

export type PublicProfileSocialId =
  | 'linkedin'
  | 'instagram'
  | 'x'
  | 'tiktok'
  | 'youtube'
  | 'snapchat'
  | 'facebook'
  | 'discord'
  | 'pinterest'
  | 'spotify'
  | 'github'
  | 'behance'
  | 'dribbble'
  | 'tabbio'
  | 'whatsapp'
  | 'email';

export type PublicProfileContactChannelId =
  | 'tabbioMessage'
  | 'whatsapp'
  | 'telegram'
  | 'slack'
  | 'email'
  | 'phone'
  | 'landline';

export type PublicProfileBlockType =
  | 'webcv'
  | 'career'
  | 'services'
  | 'website'
  | 'insights'
  | 'faqs'
  | 'links'
  | 'visit'
  | 'openroles'
  | 'team'
  | 'talentpool'
  | 'portfolio'
  | 'testimonial'
  | 'booking'
  | 'map'
  | 'pdf'
  | 'image'
  | 'video'
  | 'digitalproduct'
  | 'text'
  | 'header'
  | 'form'
  | 'music';

export type PublicProfileButtonStyle = 'fill' | 'outline' | 'soft' | 'glass';
export type PublicProfileButtonShape = 'square' | 'rounded' | 'pill';
export type PublicProfileSharePreviewBackground = 'gradient' | 'light' | 'dark';
export type PublicProfileOpenRolesLayout = 'tabs' | 'cards' | 'compact';
export type PublicProfileAnalyticsRange = '7d' | '30d' | '90d' | 'all';
export type PublicProfileAnalyticsSurface = 'profile' | 'cv' | 'share_link';
export type PublicProfileAnalyticsEventType =
  | 'view'
  | 'share'
  | 'cta_click'
  | 'block_click'
  | 'job_apply_click'
  | 'contact_click'
  | 'download';

export interface PublicProfileBlockItem {
  id: string;
  title?: string | null;
  body?: string | null;
  url?: string | null;
  imageUrl?: string | null;
  meta?: string | null;
}

export interface PublicProfileBlockConfig {
  url?: string | null;
  body?: string | null;
  buttonLabel?: string | null;
  submitEmail?: string | null;
  rolesLayout?: PublicProfileOpenRolesLayout | null;
  talentPoolEnabled?: boolean | null;
  /** Image/PDF/video poster URL: used by `image`, `pdf`, `digitalproduct`, etc. */
  imageUrl?: string | null;
  /** Per-block content rows for blocks like `faqs` and `testimonial`. */
  items?: PublicProfileBlockItem[];
}

export interface PublicProfileBlock {
  id: string;
  type: PublicProfileBlockType;
  enabled: boolean;
  order: number;
  customTitle?: string | null;
  customSubtitle?: string | null;
  config: PublicProfileBlockConfig;
}

export interface PublicProfileSocial {
  id: PublicProfileSocialId;
  url: string;
  enabled: boolean;
  order: number;
}

export interface PublicProfileContact {
  id: PublicProfileContactChannelId;
  value: string;
  enabled: boolean;
  order: number;
}

export interface PublicProfileTeamMember {
  id: string;
  name: string;
  role: string;
  bio?: string | null;
  imageUrl?: string | null;
  linkedinUrl?: string | null;
  websiteUrl?: string | null;
  enabled: boolean;
  order: number;
}

export interface PublicProfileDesign {
  theme: string;
  accent: string;
  buttonStyle: PublicProfileButtonStyle;
  buttonShape: PublicProfileButtonShape;
  sharePreviewBackground: PublicProfileSharePreviewBackground;
}

export interface PublicProfileSettings {
  hideBranding: boolean;
  sensitiveContent: boolean;
  showContactChannelsPublicly: boolean;
  hasSharedTabbio: boolean;
}

export interface PublicProfileJobPreferences {
  /** Work-arrangement types (Remote / On-site / Hybrid). */
  locationTypes: string[];
  /** Full-time / Part-time / Contract / Freelance. */
  employmentTypes: string[];
  /** "Open To Relocate" / "Not Open To Relocate". */
  relocation?: string;
  /** "Where To?" regions (Within UAE / Other GCC / Remote / Anywhere). */
  relocationAreas?: string[];
  /** Notice period (e.g. "1 Month"). */
  noticePeriod?: string;
}

export interface PublicProfileOwnerSummary {
  kind: PublicProfileOwnerKind;
  name: string;
  username?: string | null;
  slug?: string | null;
  headline?: string | null;
  bio?: string | null;
  location?: string | null;
  avatarUrl?: string | null;
  coverImageUrl?: string | null;
  website?: string | null;
  /** Whether the user is open to work: gates the public job-preferences card. */
  openToWork?: boolean;
  /**
   * True only when the owner's identity is genuinely UAE PASS verified. Drives
   * the mark next to the name: full strength when true, dimmed when false.
   * Readers see the affordance either way (decision 5 Aug 2026).
   */
  isUaePassVerified?: boolean;
  /** Bound to the displayed name; career claims are self-reported. */
  verificationScope?: 'identity_name';
  careerClaimsVerified?: false;
  /**
   * When the owner linked UAE PASS, ISO-8601, or null when unverified. Public
   * because the verification dialog on a shared CV states "Verified on <date>".
   */
  uaePassVerifiedAt?: string | null;
  /** Public job preferences, present only when open to work. */
  jobPreferences?: PublicProfileJobPreferences;
}

export interface PublicProfileTracking {
  viewPixelUrl: string;
  redirectBaseUrl: string;
}

/**
 * How a CV or profile was read (CV spine F05). One open of the page is a
 * `raw_open`; it becomes a `qualified_view` only when a person who is not the
 * owner and holds no grant on that CV opened it, counted once per viewer per
 * CV per 30 minutes. Identified crawlers, link unfurlers and developer API
 * reads are `machine_read`s. Downloads and contact requests are actions, not
 * views, and are counted as such.
 */
export type PublicCvReadKind =
  | 'raw_open'
  | 'qualified_view'
  | 'machine_read'
  | 'download'
  | 'contact';

/**
 * Why a recorded open is not a qualified view: a second open inside the
 * viewer's 30-minute window (`repeat`), the owner's own open (`owner`), a
 * collaborator with an active grant on the CV (`collaborator`), or a machine
 * (`machine`).
 */
export type PublicCvViewDisqualification =
  | 'repeat'
  | 'owner'
  | 'collaborator'
  | 'machine';

export interface PublicProfileAnalyticsTotals {
  /**
   * Qualified views: people who are not the owner and hold no grant on the
   * CV, one per viewer per CV per 30 minutes. Before the F05 cut-over this
   * was every recorded open; rows written before it still read as qualified.
   */
  views: number;
  /**
   * Every recorded open by a person, reloads and the owner's own signed-in
   * opens included. Absent from older APIs; at least `views`.
   */
  rawOpens?: number;
  /**
   * Reads by machines: identified crawlers and link unfurlers that ran the
   * page, and partner apps reading the CV through the developer API. Never
   * part of `views`. Absent from older APIs.
   */
  machineReads?: number;
  profileViews: number;
  cvViews: number;
  shareLinkViews: number;
  uniqueVisitors: number;
  shares: number;
  ctaClicks: number;
  contactClicks: number;
  downloads: number;
  blockClicks: number;
  jobApplyClicks: number;
}

export interface PublicProfileAnalyticsSeriesPoint extends PublicProfileAnalyticsTotals {
  date: string;
}

export interface PublicProfileAnalyticsSourceBreakdown {
  source: string;
  count: number;
}

export interface PublicProfileAnalyticsBlockBreakdown {
  blockId: string | null;
  blockType: PublicProfileBlockType | null;
  count: number;
}

export interface PublicProfileAnalyticsLocationBreakdown {
  code: string;
  label: string;
  count: number;
}

export interface PublicProfileAnalyticsSurfaceBreakdown {
  surface: PublicProfileAnalyticsSurface;
  label: string;
  count: number;
}

export interface PublicProfileAnalyticsReferrerBreakdown {
  host: string;
  count: number;
}

export interface PublicProfileAnalyticsCvBreakdown {
  cvId: string | null;
  cvTargetKey: string | null;
  label: string;
  count: number;
}

export interface PublicProfileAnalyticsFormatBreakdown {
  format: string;
  count: number;
}

export interface PublicProfileAnalyticsContactBreakdown {
  channel: string;
  count: number;
}

export type PublicProfileAnalyticsActorKind = 'person' | 'company' | 'anonymous';

/**
 * Who an activity came from, only when that is verified: a Tabbio member who
 * opened the CV while signed in and not browsing privately (`person`), or a
 * company that opened the profile from its Tabbio employer workspace
 * (`company`). Everyone else is `anonymous`; a person is never inferred from
 * a company, and anonymous activity carries no stable identity.
 */
export interface PublicProfileAnalyticsActor {
  kind: PublicProfileAnalyticsActorKind;
  /** User or company id to ask the actor endpoint about; null when anonymous. */
  id: string | null;
  name: string | null;
  imageUrl: string | null;
}

export interface PublicProfileAnalyticsRecentActivity {
  id: string;
  eventType: PublicProfileAnalyticsEventType;
  surface: PublicProfileAnalyticsSurface;
  source: string;
  locationLabel: string | null;
  referrerHost: string | null;
  cvId: string | null;
  cvTargetKey: string | null;
  shareLinkId: string | null;
  contactChannel: string | null;
  downloadFormat: string | null;
  createdAt: string;
  /** Absent from older APIs: treat as anonymous. */
  actor?: PublicProfileAnalyticsActor;
  /**
   * An employer opened the profile inside Tabbio rather than visiting the
   * public CV. Listed in the feed, never added to the public totals.
   */
  inTabbio?: boolean;
}

/** GET builder/analytics/actor: one actor's activity in the page's scope. */
export interface PublicProfileAnalyticsActorQuery {
  mode: PublicProfileMode;
  range?: PublicProfileAnalyticsRange;
  cvId?: string;
  kind: PublicProfileAnalyticsActorKind;
  /** Required for `person` and `company`. */
  id?: string;
}

export interface PublicProfileAnalyticsActorDetail {
  actor: PublicProfileAnalyticsActor;
  range: PublicProfileAnalyticsRange;
  cvId: string | null;
  /** Actions, not unique visitors. */
  totals: { views: number; downloads: number; shares: number };
  recentActivity: PublicProfileAnalyticsRecentActivity[];
}

/**
 * The viewer-side switch: when on, this member's views of other people's
 * CVs are recorded anonymously and earlier named views read back as
 * anonymous. Members are only ever named when they open a private share
 * link in the app while signed in; public CV pages cannot tell who is there.
 */
export interface CvViewingPrivacy {
  viewCvsPrivately: boolean;
}

export interface PublicProfileAnalyticsBreakdown {
  sources: PublicProfileAnalyticsSourceBreakdown[];
  locations: PublicProfileAnalyticsLocationBreakdown[];
  referrers: PublicProfileAnalyticsReferrerBreakdown[];
  surfaces: PublicProfileAnalyticsSurfaceBreakdown[];
  cvTargets: PublicProfileAnalyticsCvBreakdown[];
  downloadsByFormat: PublicProfileAnalyticsFormatBreakdown[];
  contacts: PublicProfileAnalyticsContactBreakdown[];
  recentActivity: PublicProfileAnalyticsRecentActivity[];
  blockClicks: PublicProfileAnalyticsBlockBreakdown[];
  ctaTargets: Array<{ source: string; count: number }>;
  series: PublicProfileAnalyticsSeriesPoint[];
}

/** Views, downloads and shares in one window of days. */
export interface PublicProfileAnalyticsWindowCounts {
  views: number;
  downloads: number;
  shares: number;
}

/**
 * The "↗ 12%" beside each metric: one window against the window of the same
 * length just before it, change = round((current - previous) / previous x
 * 100). For 7d / 30d / 90d the current window is the selected range itself.
 * For `all`, which has nothing before it, it is the last 30 days against the
 * 30 days before them: recent momentum, not an all-time growth figure.
 */
export interface PublicProfileAnalyticsComparison {
  /** Days in each window (7, 30 or 90). */
  windowDays: number;
  current: PublicProfileAnalyticsWindowCounts;
  previous: PublicProfileAnalyticsWindowCounts;
}

export interface PublicProfileAnalyticsSummary {
  range: PublicProfileAnalyticsRange;
  /** Older counts contain no visitor identity; distinct visitors and named
   * activity cover the raw window only. Present when archived counts exist. */
  history?: { rawWindowDays: number; olderHistory: 'daily_counts'; uniqueVisitorsWindowDays: number };
  /** The CV the numbers are scoped to, or null for every CV and the profile. */
  cvId?: string | null;
  totals: PublicProfileAnalyticsTotals;
  /**
   * The trend windows (see PublicProfileAnalyticsComparison). Absent from
   * older APIs, and null when it could not be read: the screen then shows
   * no trend rather than a made-up one.
   */
  comparison?: PublicProfileAnalyticsComparison | null;
  breakdown: PublicProfileAnalyticsBreakdown;
  /**
   * Which payload this is: everything, or the basic tier because the caller
   * is not entitled to the detail. `counts_only` carries the views, downloads
   * and shares totals, their daily series and the single latest activity
   * (event, time, country, surface and CV only); visitors, the breakdowns,
   * sources, referrers and the rest of the feed are omitted on the server.
   *
   * The server decides entitlement and says so here, so a client renders
   * "locked" from the answer it was given rather than re-deriving it. Clients
   * that guessed got it wrong: the API evaluates `individual.basic_analytics`
   * against the seeker plan set, while a client asking the same question from
   * inside an employer workspace is denied `wrong_tier` no matter what anyone
   * pays — which locked the tab for every employer and pointed them at an
   * upgrade that could not unlock it. `counts_only` also can't be inferred
   * from the zeros, because a genuinely quiet page returns the same numbers.
   */
  detail: 'full' | 'counts_only';
  /**
   * Employers who opened this person from inside Tabbio (search, library,
   * shortlist). Counted apart from the public numbers above, never added to
   * them: a recruiter view is not a visit to the public CV. Individual owners
   * with the detailed tier only; null otherwise.
   */
  recruiter?: PublicProfileRecruiterViews | null;
}

export interface PublicProfileRecruiterViews {
  /** Employers who opened the profile: one company on one day counts once. */
  views: number;
  /** Distinct companies behind those views. */
  companies: number;
  /**
   * Recruiter searches the person appeared in, counted like views (one
   * company, one day), and the keywords behind them. Absent from older
   * API responses.
   */
  searches?: number;
  keywords?: PublicProfileRecruiterKeyword[];
}

/**
 * A term recruiters searched with (what they typed, or a skill they filtered
 * by) while this person was in the results. Never names the company.
 */
export interface PublicProfileRecruiterKeyword {
  keyword: string;
  /** Searches with this term that returned the person (company-days). */
  searches: number;
  /** Times a recruiter then opened the profile from such a search. */
  views: number;
}

export interface PublicProfileBuilderState {
  pageId: string;
  mode: PublicProfileMode;
  owner: PublicProfileOwnerSummary;
  publicUrl: string;
  urls: PublicProfileUrls;
  design: PublicProfileDesign;
  settings: PublicProfileSettings;
  blocks: PublicProfileBlock[];
  socials: PublicProfileSocial[];
  contacts: PublicProfileContact[];
  teamMembers: PublicProfileTeamMember[];
  analytics: PublicProfileAnalyticsSummary | null;
  // Mirrors the live-page visibility check so the builder/preview can warn
  // when the page would 404 publicly. Individual: visibility === 'public'
  // && discoverable !== false. Company: published === true && status === 'active'.
  isPubliclyVisible: boolean;
}

export interface PublicProfilePublicBase {
  pageId: string;
  ownerKind: PublicProfileOwnerKind;
  publicUrl: string;
  urls: PublicProfileUrls;
  canonicalHandle: string;
  prettyUrl?: string | null;
  searchEngineIndexingAllowed: boolean;
  tracking: PublicProfileTracking;
  owner: PublicProfileOwnerSummary;
  design: PublicProfileDesign;
  settings: Omit<PublicProfileSettings, 'hasSharedTabbio'>;
  privacy: PublicProfileEffectivePrivacy;
  blocks: PublicProfileBlock[];
  socials: PublicProfileSocial[];
  contacts: PublicProfileContact[];
}

export interface PublicProfileMainCvDocument {
  isUaePassVerified?: boolean;
  uaePassVerifiedAt?: string | null;
  verificationScope?: 'identity_name';
  careerClaimsVerified?: false;
  locale?: string;
  direction?: 'ltr' | 'rtl';
  id: string;
  title: string;
  summary?: string | null;
  slug?: string | null;
  templateId: string;
  isMaster: boolean;
  updatedAt: string;
  name?: string | null;
  headline?: string | null;
  location?: string | null;
  avatarUrl?: string | null;
  contactEmail?: string | null;
  socials: SmartCVPublicSocials;
  skillGroups: SkillGroupDraft[];
  languages: string[];
  cvConfig: CVConfigData;
  /** Skill-applied section include-list + order; null/absent = default order. */
  sectionsConfig?: { order: CvSectionId[] } | null;
  highlights: CareerHighlightDraft[];
  experiences: SmartCVPublicExperience[];
  educations: SmartCVPublicEducation[];
  certifications: SmartCVPublicCertification[];
  projects: SmartCVPublicProject[];
}

export interface PublicProfilePublicUserPage extends PublicProfilePublicBase {
  ownerKind: 'user';
  urls: PublicUserProfileUrls;
  /** A transitioned CV may not fall back to live profile facts. */
  cvPublication?: 'legacy' | 'published' | 'unpublished';
  /** Only live, verified public release pointers; never private drafts. */
  publishedCvLocales?: string[];
  defaultCvLocale?: string;
  mainCv?: PublicProfileMainCvDocument;
  summary?: string | null;
  skills: string[];
  skillGroups: SkillGroupDraft[];
  languages: string[];
  cvConfig: CVConfigData;
  highlights: Array<{ id: string; title: string; description?: string | null }>;
  experiences: SmartCVPublicExperience[];
  educations: SmartCVPublicEducation[];
  certifications: SmartCVPublicCertification[];
  projects: SmartCVPublicProject[];
  services: Array<{
    id: string;
    title: string;
    description?: string | null;
    basePrice?: string | null;
    deliveryDays?: number | null;
    deliverables: string[];
  }>;
}

export interface PublicProfilePublicCompanyPage extends PublicProfilePublicBase {
  ownerKind: 'company';
  urls: PublicCompanyProfileUrls;
  tagline?: string | null;
  description?: string | null;
  industry?: string | null;
  employeeCount?: string | null;
  teamMembers: PublicProfileTeamMember[];
  openRoles: CompanyJobSummary[];
}

export type PublicProfilePublicPage =
  | PublicProfilePublicUserPage
  | PublicProfilePublicCompanyPage;

/**
 * One row of the crawlable company directory behind `/{locale}/companies`.
 *
 * Membership is identical to `companies-sitemap.xml` by construction (both are
 * produced by the same API selection), so a row here is always a company URL
 * that renders an indexable company page. Carries only display fields the
 * public company page already shows.
 */
export interface PublicCompanyDirectoryEntry {
  slug: string;
  name: string;
  logo: string | null;
  tagline: string | null;
  industry: string | null;
  location: string | null;
  /** Live, public, page-distributed roles: the set the company page lists. */
  openRoles: number;
  updatedAt: string;
}

export interface PublicCompanyDirectoryPage {
  companies: PublicCompanyDirectoryEntry[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * One row of the crawlable CV directory behind `/{locale}/profiles`.
 *
 * Membership is identical to `profiles-sitemap.xml` by construction, so a row
 * is always a CV URL the sitemap also submits. The three display fields are
 * resolved exactly as the CV page resolves its own header, a chosen main CV's
 * overrides and the owner's location setting included, so the directory never
 * shows a person differently from the page it links to.
 */
export interface PublicCvDirectoryEntry {
  handle: string;
  name: string;
  headline: string | null;
  location: string | null;
}

export interface PublicCvDirectoryPage {
  cvs: PublicCvDirectoryEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UpdatePublicProfileBlocksInput {
  mode: PublicProfileMode;
  blocks: PublicProfileBlock[];
}

export interface UpdatePublicProfileSocialsInput {
  mode: PublicProfileMode;
  socials: PublicProfileSocial[];
}

export interface UpdatePublicProfileContactsInput {
  mode: PublicProfileMode;
  contacts: PublicProfileContact[];
}

export interface UpdatePublicProfileDesignInput {
  mode: PublicProfileMode;
  design: PublicProfileDesign;
}

export interface UpdatePublicProfileSettingsInput {
  mode: PublicProfileMode;
  settings: PublicProfileSettings;
}

export interface UpdatePublicProfileTeamInput {
  mode: 'company';
  teamMembers: PublicProfileTeamMember[];
}

export interface PublicProfileAnalyticsQuery {
  mode: PublicProfileMode;
  range?: PublicProfileAnalyticsRange;
  /**
   * Scope every number to one CV: a CV id, or `main` for the Main CV (the
   * profile-built public CV). Omitted = the whole page.
   */
  cvId?: string;
}

export interface PublicProfileEventIngestInput {
  pageId: string;
  eventType: PublicProfileAnalyticsEventType;
  surface?: PublicProfileAnalyticsSurface;
  source?: string;
  blockId?: string | null;
  blockType?: PublicProfileBlockType | null;
  jobId?: string | null;
  cvId?: string | null;
  cvTargetKey?: string | null;
  shareLinkId?: string | null;
  contactChannel?: string | null;
  downloadFormat?: string | null;
  targetUrl?: string | null;
  projectId?: string | null;
  referrer?: string | null;
  metadata?: Record<string, unknown>;
}

export interface PublicProfileRenderInput {
  mode: PublicProfileMode;
  publicUrl: string;
  urls?: PublicProfileUrls;
  owner: Pick<PublicProfileOwnerSummary, 'name' | 'headline' | 'bio' | 'location' | 'website'>;
  blocks: PublicProfileBlock[];
  summary?: string | null;
  skills?: string[];
  highlights?: Array<{ id?: string; title: string; description?: string | null }>;
  experiences?: SmartCVPublicExperience[];
  projects?: Array<{
    id: string;
    title: string;
    description?: string | null;
    image?: string | null;
    link?: string | null;
    technologies?: string[];
  }>;
  services?: Array<{
    id: string;
    title: string;
    description?: string | null;
    basePrice?: string | number | null;
    deliveryDays?: number | null;
    deliverables: string[];
  }>;
  teamMembers?: PublicProfileTeamMember[];
  openRoles?: PublicProfileRenderOpenRole[];
}

export interface PublicProfileRenderProject {
  id: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  href?: string | null;
  technologies: string[];
}

export interface PublicProfileRenderService {
  id: string;
  title: string;
  description?: string | null;
  basePrice?: string | number | null;
  deliveryDays?: number | null;
  deliverables: string[];
}

export interface PublicProfileRenderOpenRole {
  id: string;
  title: string;
  description?: string | null;
  location?: string | null;
  department?: string | null;
  type?: string | null;
  workMode?: string | null;
  level?: string | null;
  tags?: string[];
  applicationCount?: number | null;
  publicSlug?: string;
  canonicalUrl?: string;
  applyUrl?: string | null;
  indexingAllowed?: boolean;
}

interface PublicProfileRenderBlockBase {
  id: string;
  type: PublicProfileBlockType;
  order: number;
  title?: string | null;
  subtitle?: string | null;
  /** Long-form description; rendered as a paragraph between subtitle and any item list. */
  body?: string | null;
  href?: string | null;
  /** Optional asset URL (image/pdf/video/music source): populated from `config.imageUrl` or `config.url`. */
  imageUrl?: string | null;
  /** Optional CTA button label override. */
  buttonLabel?: string | null;
}

// Render-block discriminated union. Each variant is exactly one `type` so
// `Extract<PublicProfileRenderBlock, {type: 'image'}>` resolves cleanly -
// merging multiple types into a single variant defeats narrowing.
interface PublicProfileRenderCvFields {
  summary?: string | null;
  skills: string[];
  highlights: Array<{ id?: string; title: string; description?: string | null }>;
  experiences: SmartCVPublicExperience[];
}

export type PublicProfileRenderBlock =
  | (PublicProfileRenderBlockBase & { type: 'webcv' } & PublicProfileRenderCvFields)
  | (PublicProfileRenderBlockBase & { type: 'career' } & PublicProfileRenderCvFields)
  | (PublicProfileRenderBlockBase & {
      type: 'website';
      website?: string | null;
      domain?: string | null;
    })
  | (PublicProfileRenderBlockBase & {
      type: 'portfolio';
      projects: PublicProfileRenderProject[];
    })
  | (PublicProfileRenderBlockBase & {
      type: 'services';
      services: PublicProfileRenderService[];
    })
  | (PublicProfileRenderBlockBase & {
      type: 'visit';
      location?: string | null;
    })
  | (PublicProfileRenderBlockBase & {
      type: 'map';
      location?: string | null;
    })
  | (PublicProfileRenderBlockBase & { type: 'header' })
  | (PublicProfileRenderBlockBase & { type: 'text' })
  | (PublicProfileRenderBlockBase & {
      type: 'form';
      submitEmail?: string | null;
    })
  | (PublicProfileRenderBlockBase & {
      type: 'openroles';
      roles: PublicProfileRenderOpenRole[];
      talentPoolEnabled?: boolean;
      rolesLayout?: PublicProfileOpenRolesLayout;
    })
  | (PublicProfileRenderBlockBase & {
      type: 'team';
      members: PublicProfileTeamMember[];
    })
  | (PublicProfileRenderBlockBase & { type: 'image' })
  | (PublicProfileRenderBlockBase & { type: 'pdf' })
  | (PublicProfileRenderBlockBase & { type: 'video' })
  | (PublicProfileRenderBlockBase & { type: 'music' })
  | (PublicProfileRenderBlockBase & { type: 'digitalproduct' })
  | (PublicProfileRenderBlockBase & { type: 'booking' })
  | (PublicProfileRenderBlockBase & { type: 'talentpool' })
  | (PublicProfileRenderBlockBase & { type: 'insights' })
  | (PublicProfileRenderBlockBase & {
      type: 'faqs';
      items: PublicProfileBlockItem[];
    })
  | (PublicProfileRenderBlockBase & {
      type: 'testimonial';
      items: PublicProfileBlockItem[];
    })
  /**
   * A list of labelled outbound links. Every row is `{ title, url }` from
   * `config.items`, so the block holds as many as the page needs — unlike
   * `website`, whose single `config.url` never justified a section of its own.
   */
  | (PublicProfileRenderBlockBase & {
      type: 'links';
      items: PublicProfileBlockItem[];
    });

function normalizePublicProfileText(value?: string | null) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export function getPublicProfileExternalUrl(value?: string | null) {
  const clean = normalizePublicProfileText(value);
  if (!clean) return null;
  if (
    clean.startsWith('http://') ||
    clean.startsWith('https://') ||
    clean.startsWith('mailto:') ||
    clean.startsWith('tel:')
  ) {
    return clean;
  }
  return `https://${clean}`;
}

interface SocialPlatformPattern {
  buildFromHandle?: (handle: string) => string;
  domains?: string[];
}

// Maps each social ID to its canonical URL pattern. When the user saves just a
// handle (e.g. "ahmed" for LinkedIn), the renderer resolves it to the full
// platform URL ("https://www.linkedin.com/in/ahmed") so the CTA actually opens
// the right profile instead of `https://ahmed` (a broken host).
const PUBLIC_PROFILE_SOCIAL_URL_PATTERNS: Record<PublicProfileSocialId, SocialPlatformPattern> = {
  linkedin: {
    buildFromHandle: (h) => `https://www.linkedin.com/in/${h}`,
    domains: ['linkedin.com', 'www.linkedin.com'],
  },
  instagram: {
    buildFromHandle: (h) => `https://www.instagram.com/${h}`,
    domains: ['instagram.com', 'www.instagram.com'],
  },
  x: {
    buildFromHandle: (h) => `https://x.com/${h}`,
    domains: ['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com'],
  },
  tiktok: {
    buildFromHandle: (h) => `https://www.tiktok.com/@${h}`,
    domains: ['tiktok.com', 'www.tiktok.com'],
  },
  youtube: {
    buildFromHandle: (h) => `https://www.youtube.com/@${h}`,
    domains: ['youtube.com', 'www.youtube.com', 'youtu.be'],
  },
  snapchat: {
    buildFromHandle: (h) => `https://www.snapchat.com/add/${h}`,
    domains: ['snapchat.com', 'www.snapchat.com'],
  },
  facebook: {
    buildFromHandle: (h) => `https://www.facebook.com/${h}`,
    domains: ['facebook.com', 'www.facebook.com', 'fb.com', 'www.fb.com'],
  },
  discord: {
    domains: ['discord.gg', 'discord.com', 'www.discord.com'],
  },
  pinterest: {
    buildFromHandle: (h) => `https://www.pinterest.com/${h}`,
    domains: ['pinterest.com', 'www.pinterest.com'],
  },
  spotify: {
    buildFromHandle: (h) => `https://open.spotify.com/user/${h}`,
    domains: ['open.spotify.com', 'spotify.com', 'www.spotify.com'],
  },
  github: {
    buildFromHandle: (h) => `https://github.com/${h}`,
    domains: ['github.com', 'www.github.com'],
  },
  behance: {
    buildFromHandle: (h) => `https://www.behance.net/${h}`,
    domains: ['behance.net', 'www.behance.net'],
  },
  dribbble: {
    buildFromHandle: (h) => `https://dribbble.com/${h}`,
    domains: ['dribbble.com', 'www.dribbble.com'],
  },
  tabbio: {
    buildFromHandle: (h) => `https://tabbio.com/${h.replace(/^@/, '')}`,
    domains: ['tabbio.com', 'www.tabbio.com'],
  },
  whatsapp: {},
  email: {},
};

function startsWithKnownDomain(value: string, domains: string[]) {
  const lower = value.toLowerCase();
  return domains.some((domain) => lower === domain || lower.startsWith(`${domain}/`));
}

/**
 * Resolve a user-typed social/contact value into the canonical platform URL.
 *
 * Handles three input shapes per platform:
 *   - full URL (`https://linkedin.com/in/ahmed`) → returned as-is
 *   - bare domain prefix (`linkedin.com/in/ahmed`) → `https://` prepended
 *   - bare handle (`ahmed` or `@ahmed`) → platform URL template applied
 *
 * Returns `null` when the value can't be resolved (empty input, email without
 * `@`, whatsapp without digits, discord without a known invite/server URL).
 */
export function buildSocialPlatformUrl(
  id: PublicProfileSocialId,
  rawValue?: string | null,
): string | null {
  const value = normalizePublicProfileText(rawValue);
  if (!value) return null;

  if (id === 'email') {
    const email = value.startsWith('mailto:') ? value.slice('mailto:'.length) : value;
    return email.includes('@') ? `mailto:${email}` : null;
  }

  if (id === 'whatsapp') {
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
    const digits = value.replace(/[^\d+]/g, '').replace(/^\+/, '');
    return digits ? `https://wa.me/${digits}` : null;
  }

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  if (value.startsWith('mailto:') || value.startsWith('tel:')) {
    return value;
  }

  const pattern = PUBLIC_PROFILE_SOCIAL_URL_PATTERNS[id];

  if (pattern.domains && startsWithKnownDomain(value, pattern.domains)) {
    return `https://${value}`;
  }

  // Strip leading @ / and trailing /, treat the rest as a handle.
  const handle = value.replace(/^[@/]+/, '').replace(/\/+$/, '');
  if (!handle) return null;

  if (pattern.buildFromHandle) {
    return pattern.buildFromHandle(handle);
  }

  // No template (e.g. discord). If the user typed a hostname-like string
  // ("discord.gg/abc"), prepending `https://` produces a working URL.
  return value.includes('.') ? `https://${value}` : null;
}

export function buildPublicProfileLocationHref(location?: string | null) {
  const clean = normalizePublicProfileText(location);
  if (!clean) return null;
  return `https://maps.google.com/?q=${encodeURIComponent(clean)}`;
}

export function getPublicProfileBlockDefaultLabel(type: PublicProfileBlockType) {
  const labels: Record<PublicProfileBlockType, string> = {
    webcv: 'Career profile',
    career: 'Career',
    services: 'Services',
    website: 'Website',
    insights: 'Insights',
    faqs: 'FAQs',
    links: 'Links',
    visit: 'Visit',
    openroles: 'Open roles',
    team: 'Team',
    talentpool: 'Talent pool',
    portfolio: 'Portfolio',
    testimonial: 'Testimonials',
    booking: 'Booking',
    map: 'Map',
    pdf: 'PDF',
    image: 'Image',
    video: 'Video',
    digitalproduct: 'Digital product',
    text: 'Text',
    header: 'Header',
    form: 'Form',
    music: 'Music',
  };

  return labels[type];
}

export function getPublicProfileUrlDisplayLabel(url?: string | null) {
  const resolved = getPublicProfileExternalUrl(url);
  if (!resolved) return null;

  try {
    const parsed = new URL(resolved);
    // `mailto:` and `tel:` have no hostname, so reading one returned '' and
    // the row rendered a blank (but still clickable) label. Both are schemes
    // `getPublicProfileExternalUrl` deliberately accepts, so the address or
    // number is the label a reader actually wants.
    if (parsed.protocol === 'mailto:' || parsed.protocol === 'tel:') {
      return decodeURIComponent(parsed.pathname) || null;
    }
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return resolved.replace(/^https?:\/\//, '').replace(/^www\./, '');
  }
}

export function buildPublicProfileRenderBlocks(
  input: PublicProfileRenderInput,
): PublicProfileRenderBlock[] {
  const renderedBlocks: PublicProfileRenderBlock[] = [];

  [...input.blocks]
    .filter((block) => block.enabled)
    .sort((left, right) => left.order - right.order)
    .forEach((block) => {
      const base = {
        id: block.id,
        type: block.type,
        order: block.order,
        title: normalizePublicProfileText(block.customTitle),
        subtitle: normalizePublicProfileText(block.customSubtitle),
        body: normalizePublicProfileText(block.config?.body),
        imageUrl: normalizePublicProfileText(block.config?.imageUrl),
        buttonLabel: normalizePublicProfileText(block.config?.buttonLabel),
      } as const;

      switch (block.type) {
        case 'webcv':
        case 'career':
          renderedBlocks.push({
            ...base,
            type: block.type,
            href:
              input.mode === 'individual' && input.urls && 'cv' in input.urls
                ? input.urls.cv
                : `${input.publicUrl.replace(/\/$/, '')}/cv`,
            summary:
              normalizePublicProfileText(input.summary) ||
              normalizePublicProfileText(input.owner.bio) ||
              normalizePublicProfileText(input.owner.headline),
            skills: input.skills ?? [],
            highlights: input.highlights ?? [],
            experiences: input.experiences ?? [],
          } satisfies PublicProfileRenderBlock);
          return;

        case 'website': {
          // The hero already links the owner's website, so mirroring it here
          // put the same URL on the page twice — for a company, setting a
          // website silently grew a whole block beneath its own hero link. A
          // company's website block now renders only when it was given a URL of
          // its own. Individual pages keep the mirror: their block has been the
          // main website CTA since before the hero link existed, and dropping
          // it would quietly change every live personal page.
          const website =
            input.mode === 'company'
              ? normalizePublicProfileText(block.config?.url)
              : normalizePublicProfileText(input.owner.website) ||
                normalizePublicProfileText(block.config?.url);
          const href = getPublicProfileExternalUrl(website);
          renderedBlocks.push({
            ...base,
            type: 'website',
            href,
            website,
            domain: getPublicProfileUrlDisplayLabel(href),
          } satisfies PublicProfileRenderBlock);
          return;
        }

        case 'portfolio':
          renderedBlocks.push({
            ...base,
            type: 'portfolio',
            projects: (input.projects ?? []).map((project) => ({
              id: project.id,
              title: project.title,
              description: normalizePublicProfileText(project.description),
              imageUrl: normalizePublicProfileText(project.image),
              href: getPublicProfileExternalUrl(project.link),
              technologies: project.technologies ?? [],
            })),
          } satisfies PublicProfileRenderBlock);
          return;

        case 'services':
          renderedBlocks.push({
            ...base,
            type: 'services',
            services: (input.services ?? []).map((service) => ({
              id: service.id,
              title: service.title,
              description: normalizePublicProfileText(service.description),
              basePrice: service.basePrice ?? null,
              deliveryDays: service.deliveryDays ?? null,
              deliverables: service.deliverables ?? [],
            })),
          } satisfies PublicProfileRenderBlock);
          return;

        case 'visit':
        case 'map': {
          const location =
            normalizePublicProfileText(input.owner.location) ||
            normalizePublicProfileText(block.config?.body) ||
            normalizePublicProfileText(block.customSubtitle);
          renderedBlocks.push({
            ...base,
            type: block.type,
            href:
              buildPublicProfileLocationHref(location) ||
              getPublicProfileExternalUrl(block.config?.url),
            location,
          } satisfies PublicProfileRenderBlock);
          return;
        }

        case 'header':
        case 'text':
          renderedBlocks.push({
            ...base,
            type: block.type,
          } satisfies PublicProfileRenderBlock);
          return;

        case 'form': {
          const submitEmail = normalizePublicProfileText(block.config?.submitEmail);
          renderedBlocks.push({
            ...base,
            type: 'form',
            href:
              getPublicProfileExternalUrl(block.config?.url) ||
              (submitEmail ? `mailto:${submitEmail}` : null),
            submitEmail,
          } satisfies PublicProfileRenderBlock);
          return;
        }

        case 'openroles':
          renderedBlocks.push({
            ...base,
            type: 'openroles',
            href: `${input.publicUrl.replace(/\/$/, '')}#open-roles`,
            roles: input.openRoles ?? [],
            talentPoolEnabled: block.config?.talentPoolEnabled ?? true,
            rolesLayout: block.config?.rolesLayout ?? 'tabs',
          } satisfies PublicProfileRenderBlock);
          return;

        case 'team':
          renderedBlocks.push({
            ...base,
            type: 'team',
            href: `${input.publicUrl.replace(/\/$/, '')}#team`,
            members: (input.teamMembers ?? [])
              .filter((member) => member.enabled)
              .sort((left, right) => left.order - right.order),
          } satisfies PublicProfileRenderBlock);
          return;

        case 'image':
        case 'pdf':
        case 'video':
        case 'music':
        case 'digitalproduct': {
          // imageUrl points at the asset (image src / pdf thumbnail / video poster).
          // url is the canonical destination: click target for pdf/video/music
          // embeds, "Buy" URL for digitalproduct.
          renderedBlocks.push({
            ...base,
            type: block.type,
            href: getPublicProfileExternalUrl(block.config?.url),
          } satisfies PublicProfileRenderBlock);
          return;
        }

        case 'booking':
        case 'talentpool':
        case 'insights': {
          renderedBlocks.push({
            ...base,
            type: block.type,
            href: getPublicProfileExternalUrl(block.config?.url),
          } satisfies PublicProfileRenderBlock);
          return;
        }

        case 'faqs':
        case 'testimonial':
        case 'links': {
          const items = Array.isArray(block.config?.items)
            ? block.config.items
                .filter((item) => item && typeof item === 'object')
                .map((item, index) => ({
                  id: item.id || `${block.id}_${index}`,
                  title: normalizePublicProfileText(item.title),
                  body: normalizePublicProfileText(item.body),
                  url: getPublicProfileExternalUrl(item.url),
                  imageUrl: normalizePublicProfileText(item.imageUrl),
                  meta: normalizePublicProfileText(item.meta),
                }))
            : [];
          renderedBlocks.push({
            ...base,
            type: block.type,
            items,
          } satisfies PublicProfileRenderBlock);
          return;
        }

        default:
          return;
      }
    });

  return renderedBlocks;
}

export function getPublishedPublicProfileBlocks(
  blocks: PublicProfileRenderBlock[],
  options: { isCompany: boolean },
): PublicProfileRenderBlock[] {
  const rendered = blocks.filter((block) => {
    if (block.type === 'website') return Boolean(block.href);
    if (block.type === 'portfolio') return block.projects.length > 0;
    if (block.type === 'services') return block.services.length > 0;
    if (block.type === 'visit' || block.type === 'map') return Boolean(block.location);
    if (block.type === 'team') return block.members.length > 0;
    if (block.type === 'text') return Boolean(block.body || block.subtitle);
    if (block.type === 'form') return Boolean(block.href || block.body || block.submitEmail);
    if (block.type === 'image') return Boolean(block.imageUrl);
    if (block.type === 'pdf') return Boolean(block.href || block.imageUrl);
    if (block.type === 'video' || block.type === 'music') return Boolean(block.href);
    // `body` counts: the section has a dedicated non-clickable card for a
    // product with nothing to link yet ("launching in October, DM for early
    // access"), so gating on href/imageUrl alone silently unpublished it.
    if (block.type === 'digitalproduct')
      return Boolean(block.href || block.imageUrl || block.body);
    if (block.type === 'booking' || block.type === 'talentpool' || block.type === 'insights') {
      return Boolean(block.href || block.body);
    }
    if (block.type === 'faqs' || block.type === 'testimonial') {
      return block.items.some((item) => item.title || item.body);
    }
    // A link row with no destination is not a link, so it does not publish the
    // block on its own the way an untitled FAQ answer would.
    if (block.type === 'links') {
      return block.items.some((item) => item.url);
    }
    return true;
  });

  return rendered;
}
